import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './styles/global.css';
import { router } from './App';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import { isRetryableError } from './utils/apiFetch';
import { logError } from './utils/log';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry once when the worker couldn't be reached or failed (5xx). A 4xx,
      // such as a missing poll, won't change on a retry, so it shows at once.
      retry: (failureCount, error) => failureCount < 1 && isRetryableError(error),
      staleTime: 5_000,
    },
  },
});

window.addEventListener('error', (event) => {
  // Ignore errors injected by browser extensions (Brave Wallet, reader mode scripts, etc.).
  // Extension-injected scripts have no filename, a chrome/safari-extension:// URL,
  // or reference browser globals the site never touches.
  const filename = event.filename ?? '';
  const msg = event.message ?? '';
  const isExtensionFile = !filename || /^(chrome|moz|safari)-extension:\/\//i.test(filename);
  const isExtensionGlobal = /window\.(ethereum|solana|firefox|chrome|safari)\./.test(msg);
  if (isExtensionFile || isExtensionGlobal) return;

  logError('window.error', event.error ?? new Error(msg), {
    filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

window.addEventListener('unhandledrejection', (event) => {
  logError('window.unhandledrejection', event.reason, {
    reasonType: typeof event.reason,
  });
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SiteSettingsProvider>
          <RouterProvider router={router} />
        </SiteSettingsProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>
);
